import { NavigationGuardNext, RouteLocationNormalized } from "vue-router"
import { BenefitTypeEnum } from "@vitable/asclepius-api-client-ts"
import { VPC_STEP } from "@common/utilities/benefit-utils"
import { ensureIsAuthenticatedOrEmbed } from "@/router/guardians"
import {
  ensureActiveEnrollmentSequence,
  ensureActiveVpcOrNonVpcBenefitElection,
  ensureAllElectionsMade,
  ensureEligibleForChoosingHealthPlan,
  ensureEligibleForSchedulingMedicareCall,
  ensurePlanRecommendationsEnabled,
  ensureSequentialBenefitTypeCompletion,
  ensureVpcElectionPresent
} from "@/modules/enrollment-new/router/guardians"
import {
  ENROLLMENT_STEP_ROUTES,
  ENROLLMENT_SUBFLOW_ROUTES,
  PLAN_SHOPPING_STEP
} from "@/modules/enrollment-new/enrollment-flow-types"
import { parseNumberQueryParam } from "@/modules/enrollment-new/router/route-utils"
import GuardianRunner from "@/router/guardian-runner"
import EnrollmentLayout from "@/layouts/enrollment-new/EnrollmentLayout.vue"
import EnrollmentFlowLayout from "@/modules/enrollment-new/pages/enrollment-flow/layout/EnrollmentFlowLayout.vue"

const redirectToEnrollNewStartAsOe = (to: RouteLocationNormalized) => ({
  name: "enroll-new-start",
  query: { ...to.query, oe: null }
})

const recBeforeEnter = (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
  GuardianRunner.run(to, from, next, [ensurePlanRecommendationsEnabled])
}

const recRouteMeta = {
  stepKey: PLAN_SHOPPING_STEP,
  parentStepKey: PLAN_SHOPPING_STEP,
  subflowBack: "parent" as const
}

export default [
  {
    path: "/enrollment-layout-enrollment",
    meta: { embeddable: true },
    component: EnrollmentLayout,
    children: [
      // START: ENROLLMENT LANDING / ROUTING
      { path: "/enroll", redirect: redirectToEnrollNewStartAsOe },
      { path: "/enroll-new", redirect: redirectToEnrollNewStartAsOe },
      {
        path: "/enroll/start",
        name: "enroll-new-start",
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureIsAuthenticatedOrEmbed])
        },
        component: () => import("@/modules/enrollment-new/pages/enrollment-router/EnrollmentRouterScreen.vue")
      },
      // FINISH: ENROLLMENT LANDING / ROUTING

      // START: POST ENROLLMENT FLOW
      {
        path: "/enroll/schedule-visit",
        name: "enrollment-schedule-visit",
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureIsAuthenticatedOrEmbed])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/post-enrollment-flow/EnrollmentFlowScheduleVisitPage.vue")
      },
      {
        path: "/enroll/download-app",
        name: "enrollment-download-app",
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureIsAuthenticatedOrEmbed])
        },
        component: () => import("@/modules/enrollment-new/pages/post-enrollment-flow/EnrollmentFlowDownloadAppPage.vue")
      }
      // FINISH: POST ENROLLMENT FLOW
    ]
  },
  // START: ENROLLMENT FLOW
  {
    path: "/enroll/benefits",
    name: "enroll-benefits-layout",
    meta: { embeddable: true },
    beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
      GuardianRunner.run(to, from, next, [
        ensureIsAuthenticatedOrEmbed,
        ensureActiveEnrollmentSequence,
        ensureActiveVpcOrNonVpcBenefitElection
      ])
    },
    component: EnrollmentFlowLayout,
    children: [
      {
        path: "/background",
        name: ENROLLMENT_STEP_ROUTES.background,
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/background/EnrollmentFlowBackgroundPage.vue")
      },
      {
        path: "/dependents",
        name: ENROLLMENT_STEP_ROUTES.dependents,
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/dependents/EnrollmentFlowDependentsPage.vue")
      },
      // Per-benefit-type pages (Medical → VPC → Dental → Vision)
      {
        path: "/benefits/medical/:year",
        name: ENROLLMENT_STEP_ROUTES[BenefitTypeEnum.Medical],
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureSequentialBenefitTypeCompletion])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/medical-benefit/EnrollmentFlowMedicalBenefitPage.vue"),
        meta: { benefitType: BenefitTypeEnum.Medical },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year
        })
      },
      {
        path: "/benefits/medical/:year/plan-shopping-intro",
        name: ENROLLMENT_SUBFLOW_ROUTES.ichraShopIntro,
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [
            ensureSequentialBenefitTypeCompletion,
            ensureEligibleForChoosingHealthPlan
          ])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/medical-benefit/EnrollmentFlowIchraShopIntroPage.vue"),
        meta: { stepKey: PLAN_SHOPPING_STEP, parentStepKey: PLAN_SHOPPING_STEP },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year,
          enrollmentId: route.query.enrollmentId,
          employerAllowanceInCents: parseNumberQueryParam(route.query.employerAllowanceInCents)
        })
      },
      {
        path: "/benefits/medical/:year/choose-health-plan",
        name: ENROLLMENT_SUBFLOW_ROUTES.chooseHealthPlan,
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [
            ensureSequentialBenefitTypeCompletion,
            ensureEligibleForChoosingHealthPlan
          ])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/medical-benefit/EnrollmentFlowChooseHealthPlanPage.vue"),
        meta: {
          stepKey: PLAN_SHOPPING_STEP,
          parentStepKey: PLAN_SHOPPING_STEP,
          subflowBack: "parent"
        },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year,
          enrollmentId: route.query.enrollmentId,
          employerAllowanceInCents: parseNumberQueryParam(route.query.employerAllowanceInCents)
        })
      },
      {
        path: "/benefits/medical/:year/choose-health-plan/carrier-questions",
        name: ENROLLMENT_SUBFLOW_ROUTES.carrierQuestions,
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [
            ensureSequentialBenefitTypeCompletion,
            ensureEligibleForChoosingHealthPlan
          ])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/medical-benefit/EnrollmentFlowCarrierQuestionsPage.vue"),
        meta: {
          stepKey: PLAN_SHOPPING_STEP,
          parentStepKey: PLAN_SHOPPING_STEP,
          subflowBack: "parent"
        },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year,
          enrollmentId: route.query.enrollmentId,
          employerAllowanceInCents: parseNumberQueryParam(route.query.employerAllowanceInCents)
        })
      },
      {
        path: "/benefits/medical/:year/schedule-medicare-call",
        name: ENROLLMENT_SUBFLOW_ROUTES.scheduleMedicareCall,
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [
            ensureSequentialBenefitTypeCompletion,
            ensureEligibleForSchedulingMedicareCall
          ])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/medical-benefit/EnrollmentFlowScheduleMedicareCallPage.vue"),
        meta: {
          stepKey: PLAN_SHOPPING_STEP,
          parentStepKey: PLAN_SHOPPING_STEP,
          subflowBack: "previous"
        },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year,
          enrollmentId: route.query.enrollmentId,
          employerAllowanceInCents: parseNumberQueryParam(route.query.employerAllowanceInCents)
        })
      },
      {
        path: "/benefits/vitable-primary-care",
        name: ENROLLMENT_STEP_ROUTES[VPC_STEP],
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureVpcElectionPresent, ensureSequentialBenefitTypeCompletion])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/vpc-benefit/EnrollmentFlowVPCBenefitPage.vue"),
        meta: { benefitType: VPC_STEP }
      },
      {
        path: "/benefits/dental/:year",
        name: ENROLLMENT_STEP_ROUTES[BenefitTypeEnum.Dental],
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureSequentialBenefitTypeCompletion])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/dental-benefit/EnrollmentFlowDentalBenefitPage.vue"),
        meta: { benefitType: BenefitTypeEnum.Dental },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year
        })
      },
      {
        path: "/benefits/vision/:year",
        name: ENROLLMENT_STEP_ROUTES[BenefitTypeEnum.Vision],
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureSequentialBenefitTypeCompletion])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/vision-benefit/EnrollmentFlowVisionBenefitPage.vue"),
        meta: { benefitType: BenefitTypeEnum.Vision },
        props: (route: RouteLocationNormalized) => ({
          year: route.params.year
        })
      },
      {
        path: "/enroll/recommendation/gate",
        name: ENROLLMENT_SUBFLOW_ROUTES.recGate,
        beforeEnter: recBeforeEnter,
        component: () => import("@/modules/enrollment-new/pages/enrollment-flow/pages/recommendation/GatePage.vue"),
        // Gate is a branching decision point, not a flow step — no subflowBack
        meta: { stepKey: PLAN_SHOPPING_STEP, parentStepKey: PLAN_SHOPPING_STEP }
      },
      {
        path: "/enroll/recommendation/survey",
        name: ENROLLMENT_SUBFLOW_ROUTES.recSurvey,
        beforeEnter: recBeforeEnter,
        component: () => import("@/modules/enrollment-new/pages/enrollment-flow/pages/recommendation/SurveyPage.vue"),
        meta: recRouteMeta
      },
      {
        path: "/enroll/recommendation/thinking",
        name: ENROLLMENT_SUBFLOW_ROUTES.recThinking,
        beforeEnter: recBeforeEnter,
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/recommendation/ThinkingScreen.vue"),
        meta: recRouteMeta
      },
      {
        path: "/enroll/recommendation/result",
        name: ENROLLMENT_SUBFLOW_ROUTES.recResult,
        beforeEnter: recBeforeEnter,
        component: () => import("@/modules/enrollment-new/pages/enrollment-flow/pages/recommendation/RecPage.vue"),
        meta: recRouteMeta
      },
      {
        path: "/sign-forms",
        name: ENROLLMENT_STEP_ROUTES["sign-forms"],
        beforeEnter: (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
          GuardianRunner.run(to, from, next, [ensureAllElectionsMade])
        },
        component: () =>
          import("@/modules/enrollment-new/pages/enrollment-flow/pages/sign-forms/EnrollmentFlowSignFormsPage.vue")
      }
    ]
  }
  // FINISH: ENROLLMENT FLOW
]
